import { useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { TextInput, Flex, Loader, MultiSelect, Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { FiltersBlock, PhoneInput } from '@shared/ui';
import { IconCalendar } from '@tabler/icons-react';
import { type TableFilterField } from './types';
import { getDefaultQueryFilter } from './helpers/getDefaultQueryFilter';
import { RangeInput } from './inputs/RangeInput';
import { RegionTagsInput } from '@entities/region';
import { CompanySelect } from '@entities/company';
import { ActivitySphereTagsInput } from '@entities/activitySphere';
import { getFilterStartOfDay, getFilterEndOfDay } from '@shared/helpers';
import { RecruiterSelect } from '@entities/recruiter/components/RecruiterSelect';

export type TableFiltersProps<T> = {
  config: TableFilterField[];
  initialValues: T;
  setFilter: (filter: any) => void;
  getQueryFilter?: (values: T) => any; // функция для формирования фильтра для API
  FiltersBlockProps?: React.ComponentProps<typeof FiltersBlock>;
  pageSize?: number;
};

/**
 * Универсальный компонент фильтров для таблиц (TableFilters)
 *
 * Позволяет быстро создавать фильтры для таблиц с помощью массива-конфига полей.
 * Поддерживает типы: text, select, date-range, date-picker, phone, number, select-recruiter.
 * Сохраняет фильтры в URL, синхронизирует с формой, формирует queryFilter для API.
 * Поддерживает отдельные ключи для API через поле apiKey.
 *
 * @template T Тип значений формы (обычно Record<string, any>)
 * @param {Object} props
 * @param {TableFilterField[]} props.config - Массив-конфиг полей фильтра (см. ниже)
 * @param {T} props.initialValues - Начальные значения для формы фильтра
 * @param {(filter: any) => void} props.setFilter - Функция для передачи фильтра наверх (например, в useGet...)
 * @param {(values: T) => any} [props.getQueryFilter] - Кастомная функция для формирования фильтра для API (по умолчанию getDefaultQueryFilter)
 * @param {Object} [props.FiltersBlockProps] - Пропсы для FiltersBlock (например, стили)
 *
 * @example
 * // Конфиг для фильтра сотрудников
 * const employeeFiltersConfig = [
 *   { name: 'id', label: 'ID', type: 'number' },
 *   { name: 'regionName', label: 'Город', type: 'text', apiKey: 'region.name' }, // используем apiKey для API
 *   { name: 'isActive', label: 'Активность', type: 'select', options: ACTIVITY_OPTIONS, clearable: true },
 *   { name: 'recruiterId', label: 'Рекрутер', type: 'select-recruiter', clearable: true },
 * ];
 *
 * // Начальные значения
 * const initialValues = { id: '', regionName: '', isActive: '', recruiterId: '' };
 *
 * // Использование
 * <TableFilters
 *   config={employeeFiltersConfig}
 *   initialValues={initialValues}
 *   setFilter={setFilter}
 * />
 *
 * // В setFilter попадёт объект для API:
 * // { id: { items: ['123'], operator: 'or' }, 'region.name': { items: ['Москва'], operator: 'or' } }
 */

export function TableFilters<T extends Record<string, any>>({
  config,
  initialValues,
  setFilter,
  getQueryFilter,
  FiltersBlockProps,
  pageSize,
}: TableFiltersProps<T>) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const form = useForm<T>({
    initialValues,
  });

  // Синхронизация с URL
  useEffect(() => {
    const newValues: Partial<T> = {};
    let hasUrlFilters = false;

    config.forEach(field => {
      if (field.type === 'date-range') {
        const from = searchParams.get(`${field.name}From`);
        const to = searchParams.get(`${field.name}To`);

        if (from || to) {
          hasUrlFilters = true;
        }

        newValues[field.name as keyof T] = [
          from ? new Date(from) : null,
          to ? new Date(to) : null,
        ] as any;
      } else if (
        field.type === 'select-company' ||
        field.type === 'select-recruiter'
      ) {
        const value = searchParams.get(field.name);
        if (value) {
          hasUrlFilters = true;
        }
        newValues[field.name as keyof T] = (value ||
          initialValues[field.name]) as any;
      } else if (
        field.type === 'select-city' ||
        field.type === 'select-activity-sphere' ||
        field.type === 'multi-select'
      ) {
        const value = searchParams.get(field.name);
        if (value) {
          hasUrlFilters = true;
        }
        if (value) {
          newValues[field.name as keyof T] = value.split(',') as any;
        } else {
          newValues[field.name as keyof T] = initialValues[field.name] as any;
        }
      } else if (field.type === 'date-picker') {
        const date = searchParams.get(field.name);
        if (date) {
          hasUrlFilters = true;
        }
        newValues[field.name as keyof T] = (
          date ? new Date(date) : null
        ) as any;
      } else if (field.type === 'range') {
        const fromValue = searchParams.get(field.fromName);
        const toValue = searchParams.get(field.toName);
        if (fromValue || toValue) {
          hasUrlFilters = true;
        }
        newValues[field.fromName as keyof T] = (
          fromValue ? Number(fromValue) : initialValues[field.fromName]
        ) as any;
        newValues[field.toName as keyof T] = (
          toValue ? Number(toValue) : initialValues[field.toName]
        ) as any;
      } else if (field.type === 'number') {
        const value = searchParams.get(field.name);
        if (value) {
          hasUrlFilters = true;
        }
        newValues[field.name as keyof T] = (
          value ? Number(value) : initialValues[field.name]
        ) as any;
      } else {
        const value = searchParams.get(field.name);
        if (value) {
          hasUrlFilters = true;
        }
        newValues[field.name as keyof T] = (value ||
          initialValues[field.name]) as any;
      }
    });

    const updatedValues = { ...initialValues, ...newValues };
    form.setValues(updatedValues);

    if (hasUrlFilters || pageSize !== undefined) {
      let queryFilter;

      if (getQueryFilter) {
        queryFilter = getQueryFilter(updatedValues);
      } else {
        queryFilter = getDefaultQueryFilter(updatedValues, config);
      }

      setFilter(queryFilter);
    }
  }, [searchParams]);

  const handleSubmit = (values: T) => {
    const newSearchParams = new URLSearchParams();

    config.forEach(field => {
      const value = values[field.name as keyof T];

      if (field.type === 'date-range' && Array.isArray(value)) {
        if (value[0]) {
          newSearchParams.set(
            `${field.name}From`,
            getFilterStartOfDay(value[0])
          );
        }
        if (value[1]) {
          newSearchParams.set(`${field.name}To`, getFilterEndOfDay(value[1]));
        }
      } else if (
        (field.type === 'select-city' ||
          field.type === 'select-activity-sphere' ||
          field.type === 'multi-select') &&
        Array.isArray(value)
      ) {
        if (value.length > 0) {
          newSearchParams.set(field.name, value.join(','));
        }
      } else if (
        (field.type === 'select-company' ||
          field.type === 'select-recruiter') &&
        value
      ) {
        const entity = value.id ? value.id.toString() : value.toString();
        newSearchParams.set(field.name, entity);
      } else if (field.type === 'date-picker' && value) {
        newSearchParams.set(field.name, (value as Date).toISOString());
      } else if (field.type === 'number' && value !== '' && value !== null) {
        newSearchParams.set(field.name, String(value));
      } else if (field.type === 'range') {
        const fromValue = values[field.fromName as keyof T];
        const toValue = values[field.toName as keyof T];

        if (fromValue !== '' && fromValue !== null && fromValue !== undefined) {
          newSearchParams.set(field.fromName, String(fromValue));
        }
        if (toValue !== '' && toValue !== null && toValue !== undefined) {
          newSearchParams.set(field.toName, String(toValue));
        }
      } else if (value) {
        newSearchParams.set(field.name, String(value));
      }
    });
    newSearchParams.set('opened', 'true');
    if (pageSize !== undefined) {
      newSearchParams.set('page', '1');
      newSearchParams.set('perPage', String(pageSize));
    }

    navigate({ search: newSearchParams.toString() }, { replace: true });

    let queryFilter = values;

    if (getQueryFilter) {
      queryFilter = getQueryFilter(values);
    } else {
      queryFilter = getDefaultQueryFilter(values, config);
    }

    setFilter(queryFilter);
  };

  const handleReset = () => {
    form.reset();
    form.setValues(initialValues);

    setFilter({});

    const next = new URLSearchParams();
    if (pageSize !== undefined) {
      next.set('page', '1');
      next.set('perPage', String(pageSize));
    }
    navigate({ search: next.toString() }, { replace: true });
  };

  const activeFiltersCount = useMemo(() => {
    return Object.values(form.values).filter(
      v =>
        v !== '' &&
        v !== null &&
        v !== undefined &&
        !(Array.isArray(v) && v.every(i => i === null))
    ).length;
  }, [form.values]);

  const defaultOpened = searchParams.get('opened') === 'true';

  const handleToggle = (isOpened: boolean) => {
    if (isOpened && searchParams.get('opened') === 'true') {
      searchParams.delete('opened');

      navigate({ search: searchParams.toString() }, { replace: true });
    }
  };

  return (
    <FiltersBlock
      onSubmit={form.onSubmit(handleSubmit)}
      onReset={handleReset}
      onToggle={handleToggle}
      activeFiltersCount={activeFiltersCount}
      defaultOpened={defaultOpened}
      {...FiltersBlockProps}
    >
      <Flex wrap='wrap' gap={16} rowGap={16}>
        {config.map(field => {
          switch (field.type) {
            case 'text':
              return (
                <TextInput
                  key={form.key(field.name)}
                  label={field.label}
                  {...form.getInputProps(field.name)}
                  placeholder={field.placeholder || 'Все'}
                  miw={245}
                  w={245}
                  variant='filled'
                />
              );
            case 'select':
              return (
                <Select
                  key={form.key(field.name)}
                  label={field.label}
                  data={field.options}
                  searchable={field.searchable}
                  clearable={field.clearable}
                  {...form.getInputProps(field.name)}
                  placeholder={field.placeholder || 'Все'}
                  miw={245}
                  w={245}
                  variant='filled'
                />
              );
            case 'multi-select': {
              const value = form.values[field.name];
              const placeholder =
                Array.isArray(value) && value.length > 0
                  ? ''
                  : field.placeholder || 'Все';

              return (
                <MultiSelect
                  key={form.key(field.name)}
                  label={field.label}
                  data={field.options}
                  clearable={field.clearable}
                  disabled={field.disabled || field.loading}
                  rightSection={
                    field.loading ? <Loader size='xs' /> : undefined
                  }
                  {...form.getInputProps(field.name)}
                  placeholder={placeholder}
                  miw={field.minWidth ?? 245}
                  w='auto'
                  variant='filled'
                />
              );
            }
            case 'select-activity-sphere':
              return (
                <ActivitySphereTagsInput
                  placeholder={field.placeholder}
                  label={field.label}
                  clearable={field.clearable}
                  maw={250}
                  key={form.key(field.name)}
                  {...form.getInputProps(field.name)}
                  variant='filled'
                />
              );
            case 'select-city':
              return (
                <RegionTagsInput
                  label={field.label}
                  clearable={field.clearable}
                  maw={250}
                  key={form.key(field.name)}
                  {...form.getInputProps(field.name)}
                  error={(form.errors as any)[field.name]}
                />
              );
            case 'select-company':
              return (
                <CompanySelect
                  placeholder={field.placeholder || 'Все'}
                  label={field.label}
                  variant='filled'
                  clearable={field.clearable}
                  key={form.key(field.name)}
                  {...form.getInputProps(field.name)}
                  error={form.errors.company}
                />
              );
            case 'select-recruiter':
              return (
                <RecruiterSelect
                  placeholder={field.placeholder || 'Все'}
                  label={field.label}
                  variant='filled'
                  clearable={field.clearable}
                  key={form.key(field.name)}
                  {...form.getInputProps(field.name)}
                  error={form.errors.recruiter}
                />
              );
            case 'range':
              return (
                <RangeInput
                  key={`${field.fromName}-${field.toName}`}
                  form={form}
                  fromName={field.fromName}
                  toName={field.toName}
                  label={field.label}
                  placeholderFrom={field.placeholderFrom}
                  placeholderTo={field.placeholderTo}
                />
              );
            case 'date-range':
              return (
                <DatePickerInput
                  key={form.key(field.name)}
                  type='range'
                  label={field.label}
                  allowSingleDateInRange
                  {...form.getInputProps(field.name)}
                  placeholder={field.placeholder || 'Все'}
                  miw={245}
                  w={245}
                  variant='filled'
                  leftSection={
                    <IconCalendar
                      size={16}
                      color='var(--mantine-color-primary-7)'
                    />
                  }
                />
              );
            case 'date-picker':
              return (
                <DatePickerInput
                  key={form.key(field.name)}
                  label={field.label}
                  value={form.values[field.name] || null}
                  onChange={form.getInputProps(field.name).onChange}
                  placeholder={field.placeholder || 'Все'}
                  miw={245}
                  w={245}
                  variant='filled'
                  leftSection={
                    <IconCalendar
                      size={16}
                      color='var(--mantine-color-primary-7)'
                    />
                  }
                />
              );
            case 'phone':
              return (
                <PhoneInput
                  key={form.key(field.name)}
                  label={field.label}
                  value={form.values[field.name] || ''}
                  onChange={form.getInputProps(field.name).onChange}
                  placeholder={field.placeholder || 'Все'}
                  miw={245}
                  w={245}
                  variant='filled'
                />
              );
            case 'number':
              return (
                <TextInput
                  key={form.key(field.name)}
                  label={field.label}
                  {...form.getInputProps(field.name)}
                  placeholder={field.placeholder || 'Все'}
                  miw={245}
                  w={245}
                  variant='filled'
                  type='number'
                  min={0}
                />
              );
            default:
              return null;
          }
        })}
      </Flex>
    </FiltersBlock>
  );
}
